import { AppBar, Box, Button, Container, Link, Typography } from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { BrandWordmark } from '@/components/BrandWordmark';
import { useAuthContext } from '@/context/AuthContext';
import { ROUTES } from '@/shared/routes';
import { PAGE_GUTTER, PAGE_MAX_WIDTH } from '@/styles/layout';

// Header height: 64 px on a phone, 80 px from `md`.
export const HEADER_HEIGHT = { xs: 64, md: 80 };
// A signed-out phone shows Marketplace, About us and How we work in a second row.
const MOBILE_NAV_HEIGHT = 48;

interface SiteHeaderProps {
  // Shown before the logo, e.g. the menu button.
  leading?: ReactNode;
  // Shown on the right, before the account link, e.g. the notification bell.
  actions?: ReactNode;
}

function initialsOf(email?: string): string {
  const name = (email ?? '').split('@')[0].replace(/[^a-zA-Z0-9]/g, '');

  return (name.slice(0, 2) || '?').toUpperCase();
}

const navLinkSx = {
  whiteSpace: 'nowrap',
  fontSize: 14,
  letterSpacing: '0.04em',
  color: 'ig.textMuted',
  textDecoration: 'none',
  borderBottom: '1px solid transparent',
  pb: 0.5,
  '&:hover': { color: 'text.primary' },
  '&[aria-current="page"]': {
    color: 'text.primary',
    borderBottomColor: 'primary.main',
  },
} as const;

export function SiteHeader({ leading, actions }: SiteHeaderProps) {
  const { isAuthenticated, account } = useAuthContext();

  // A signed-in phone header has no room for the three links: the footer carries them.
  const navDisplay = isAuthenticated ? { xs: 'none', md: 'flex' } : 'flex';

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        top: 0,
        backgroundColor: 'background.default',
        backgroundImage: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={leading ? undefined : { maxWidth: PAGE_MAX_WIDTH, mx: 'auto' }}
      >
        <Box
          sx={{
            display: { xs: 'grid', md: 'flex' },
            gridTemplateColumns: '1fr auto',
            gridTemplateRows: isAuthenticated
              ? `${HEADER_HEIGHT.xs}px`
              : `${HEADER_HEIGHT.xs}px ${MOBILE_NAV_HEIGHT}px`,
            alignItems: 'center',
            height: { md: HEADER_HEIGHT.md },
            px: leading ? { xs: 2, md: '24px' } : PAGE_GUTTER,
            columnGap: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 } }}>
            {leading}
            <Link
              component={RouterLink}
              to="/"
              aria-label="Inside Gallery"
              sx={{ display: 'flex' }}
            >
              <BrandWordmark sx={{ width: { xs: 72, md: 92 } }} />
            </Link>
          </Box>
          <Box
            component="nav"
            aria-label="Main"
            sx={{
              display: navDisplay,
              gap: { xs: 3, md: 4 },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              alignItems: 'center',
              overflow: 'hidden',
              gridColumn: '1 / -1',
              gridRow: 2,
              height: { xs: MOBILE_NAV_HEIGHT, md: 'auto' },
              borderTop: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
              width: { xs: '100%', md: 'auto' },
              ml: { md: 2 },
              order: { md: 1 },
            }}
          >
            <Link component={RouterLink} to="/marketplace" sx={navLinkSx}>
              Marketplace
            </Link>
            <Link component={RouterLink} to={ROUTES.about as string} sx={navLinkSx}>
              About us
            </Link>
            <Link component={RouterLink} to={ROUTES.howItWorks as string} sx={navLinkSx}>
              How we work
            </Link>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              ml: { md: 'auto' },
              order: { md: 2 },
              gridColumn: 2,
              gridRow: 1,
              justifySelf: 'end',
            }}
          >
            {actions}
            {isAuthenticated ? (
              <Link
                component={RouterLink}
                to={ROUTES.dashboard as string}
                aria-label="My account"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  color: 'text.primary',
                  fontSize: 14,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                  }}
                >
                  {initialsOf(account?.email)}
                </Box>
                <Typography
                  component="span"
                  sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: 14 }}
                >
                  My account
                </Typography>
              </Link>
            ) : (
              <Button
                component={RouterLink}
                to="/auth/login"
                variant="outlined"
                sx={{
                  borderRadius: '2px',
                  color: 'text.primary',
                  borderColor: 'ig.rule',
                  fontWeight: 400,
                  fontSize: { xs: 13, md: 14 },
                  px: { xs: 2, md: 2.5 },
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'transparent' },
                }}
              >
                Sign in
              </Button>
            )}
          </Box>
        </Box>
      </Container>
    </AppBar>
  );
}

export default SiteHeader;
